import React, { useState, useRef, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useSalon } from '../contexts/SalonContext'
import salonLogo from '../assets/salon-logo3.png'
import Sidebar from './Sidebar'
import SalonBranchDropdown from './SalonBranchDropdown'
import { savePreferredSalon } from '../services/preferredSalonService'

const Navigation = ({ 
  user, 
  appointments = [], 
  favorites = { specialists: [], services: [] }, 
  favoriteAppointments = [],
  onAuthClick, 
  onAccountClick,
  // Sidebar props
  sidebarProps = {},
  // Notifications
  unreadNotificationCount = 0,
  onNotificationsClick
}) => {
  const [mobileMenu, setMobileMenu] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarType, setSidebarType] = useState(null) // 'favorites', 'bookings', or 'notifications'
  const [activeSection, setActiveSection] = useState('home')
  const userMenuRef = useRef(null)
  const navigate = useNavigate()
  const { signOut, profile } = useAuth()
  const { salons, selectedSalon, selectSalon, salonId } = useSalon()
  const location = useLocation()

  const profileImageUrl =
    profile?.profile_image_url ||
    user?.user_metadata?.avatar_url ||
    user?.user_metadata?.picture ||
    null
  const [avatarFailed, setAvatarFailed] = useState(false)

  useEffect(() => {
    setAvatarFailed(false)
  }, [profileImageUrl])

  // Treat any /dashboard path as "dashboard route" so we can avoid duplicating sidebars there
  const isDashboardRoute = location.pathname.startsWith('/dashboard')
  const isResetPasswordRoute =
    location.pathname === '/reset-password' || location.pathname === '/resetpassword'
  const isVerifyResetCodeRoute = location.pathname === '/verify-reset-code'
  const isRecoveryAuthRoute = isResetPasswordRoute || isVerifyResetCodeRoute
  // Recovery flow creates a temporary session — don't show logged-in UI until password is changed
  const showAuthenticatedNav = user && !isRecoveryAuthRoute

  const clearServicesSelection = () => {
    try {
      localStorage.removeItem('servicesBookingSummary')
    } catch (e) {
      // no-op
    }
    window.dispatchEvent(new Event('clearServicesBookingSummary'))
  }

  const handleBranchSelect = (next) => {
    clearServicesSelection()
    selectSalon(next)
    // Persist as preferred branch so it stays consistent if localStorage is cleared
    if (user?.id && next?.id) {
      void savePreferredSalon(next.id)
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        console.log('Clicked outside, closing dropdown')
        setShowUserMenu(false)
      }
    }

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserMenu])

  useEffect(() => {
    const openBookingsSidebar = () => {
      setSidebarType('bookings')
      setSidebarOpen(true)
    }
    const openFavoritesSidebar = () => {
      setSidebarType('favorites')
      setSidebarOpen(true)
    }
    const openSupportFromNotification = () => {
      setSidebarOpen(false)
      setShowUserMenu(false)
      navigate('/dashboard?tab=support')
    }
    const openProfileFromNotification = () => {
      setSidebarOpen(false)
      setShowUserMenu(false)
      navigate('/dashboard?tab=profile')
    }
    window.addEventListener('openBookingsSidebar', openBookingsSidebar)
    window.addEventListener('openFavoritesSidebar', openFavoritesSidebar)
    window.addEventListener('openSupportFromNotification', openSupportFromNotification)
    window.addEventListener('openProfileFromNotification', openProfileFromNotification)
    return () => {
      window.removeEventListener('openBookingsSidebar', openBookingsSidebar)
      window.removeEventListener('openFavoritesSidebar', openFavoritesSidebar)
      window.removeEventListener('openSupportFromNotification', openSupportFromNotification)
      window.removeEventListener('openProfileFromNotification', openProfileFromNotification)
    }
  }, [navigate])

  const handleLogout = async () => {
    console.log('🚪 Logout button clicked in Navigation')
    
    // Force logout after 3 seconds no matter what
    const forceLogoutTimer = setTimeout(() => {
      console.log('⚠️ Force logout timeout - logging out anyway!')
      localStorage.clear()
      sessionStorage.clear()
      window.location.href = '/'
    }, 3000)
    
    try {
      console.log('🔄 Calling signOut...')
      const result = await signOut()
      console.log('📤 SignOut result:', result)
      
      // Clear localStorage
      console.log('🗑️ Clearing localStorage...')
      localStorage.clear()
      sessionStorage.clear()
      
      // Cancel force logout since we're doing it properly
      clearTimeout(forceLogoutTimer)
      
      console.log('🏠 Navigating to home...')
      // Navigate to home
      window.location.href = '/'
      
      console.log('✅ Logout complete')
    } catch (error) {
      console.error('❌ Error logging out:', error)
      // Cancel force logout timer
      clearTimeout(forceLogoutTimer)
      // Force logout anyway
      localStorage.clear()
      sessionStorage.clear()
      window.location.href = '/'
    }
  }

  const handleAccountClick = () => {
    // This function is not used anymore since we have dropdown
    // Keep it for backward compatibility
    if (user) {
      navigate('/dashboard')
    } else {
      onAuthClick()
    }
  }

  const handleNavClick = (e, sectionId) => {
    e.preventDefault()
    const currentPath = window.location.pathname

    // Services opens a dedicated page (same as mobile All Services)
    if (sectionId === 'services') {
      setActiveSection('services')
      setMobileMenu(false)
      if (currentPath !== '/services') navigate('/services')
      else window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    setActiveSection(sectionId)
    
    if (currentPath !== '/') {
      // If not on home page, navigate to home first
      navigate('/', { state: { scrollTo: sectionId } })
    } else {
      // Already on home page, just scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      }, 50)
    }
    setMobileMenu(false)
  }

  // Track active section based on scroll position (only on home page)
  useEffect(() => {
    const handleScroll = () => {
      if (window.location.pathname !== '/') {
        // Don't highlight sections on non-home routes
        return
      }

      // Use requestAnimationFrame to batch layout reads and avoid forced reflows
      requestAnimationFrame(() => {
        const sectionIds = ['home', 'services', 'team', 'about', 'reviews']
        
        // Batch all layout reads together to avoid forced reflows
        const scrollY = window.scrollY || window.pageYOffset
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight

        let bestSection = 'home'
        let bestScore = Number.POSITIVE_INFINITY

        // Collect all elements first, then read their layout properties together
        const elements = sectionIds.map(id => ({
          id,
          el: document.getElementById(id)
        })).filter(item => item.el !== null)

        // Batch all getBoundingClientRect calls
        const rects = elements.map(item => ({
          ...item,
          rect: item.el.getBoundingClientRect()
        }))

        // Now process all rects together
        rects.forEach(({ id, rect }) => {
          // Compute distance of section's top from top of viewport, adjusted by navbar height (~80px)
          const sectionTop = rect.top + scrollY
          const offsetTop = sectionTop - 80
          const distance = Math.abs(scrollY - offsetTop)

          // Also consider if section is currently within viewport
          const inView = rect.top < viewportHeight * 0.6 && rect.bottom > viewportHeight * 0.2
          const score = inView ? distance * 0.5 : distance

          if (score < bestScore) {
            bestScore = score
            bestSection = id
          }
        })

        setActiveSection(bestSection)
      })
    }

    // Only run on home page
    if (window.location.pathname === '/') {
      handleScroll()
      window.addEventListener('scroll', handleScroll)
      window.addEventListener('resize', handleScroll)
    }

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [])

  const getNavItemClasses = (id) => {
    const path = window.location.pathname
    const isActive =
      id === 'services'
        ? path === '/services' || (path === '/' && activeSection === 'services')
        : activeSection === id && path === '/'
    return [
      'px-4 py-2 rounded-full text-sm lg:text-base font-medium cursor-pointer relative group transition-all duration-200',
      isActive
        ? 'text-primary bg-white shadow-sm'
        : 'text-gray-700 hover:text-primary hover:bg-white'
    ].join(' ')
  }

  return (
    <>
    <nav className="bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-100 fixed w-full top-0 z-50">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8">
        <div className="flex items-center h-16 sm:h-20 w-full">
          <div className="flex items-center flex-shrink-0">
            <Link
              to="/"
              onClick={() => {
                // Keep services/professionals draft (same as page reload); only close menus.
                setMobileMenu(false)
                setShowUserMenu(false)
              }}
              className="flex items-center space-x-3 group"
            >
              <div className="relative">
                <img 
                  src={salonLogo}
                  alt={`${selectedSalon?.name || 'Timeless'} logo`} 
                  width="166"
                  height="48"
                  className="h-10 w-auto sm:h-12 object-contain transition-transform duration-300 group-hover:scale-105"
                  onError={(e) => {
                    console.error('Failed to load logo:', e.target.src)
                    // Fallback to public path
                    e.target.src = "/salon-logo3.png"
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-purple-400/20 to-pink-400/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></div>
              </div>
            </Link>
            {salons.length > 1 && (
              <SalonBranchDropdown
                className="hidden sm:block ml-3"
                salons={salons}
                selectedSalon={selectedSalon}
                onSelect={handleBranchSelect}
              />
            )}
          </div>
          
          {/* Desktop Menu — hidden on password reset page */}
          {!isRecoveryAuthRoute && (
          <div className="hidden md:flex flex-1 justify-center">
            <div className="flex items-center space-x-1 lg:space-x-2 bg-gray-50/80 rounded-full px-2 py-1 backdrop-blur-sm">
              <a 
                href="#home" 
                onClick={(e) => handleNavClick(e, 'home')} 
                className={getNavItemClasses('home')}
              >
                <span className="relative z-10">Home</span>
                <span
                  className={[
                    'absolute inset-0 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full transition-opacity duration-200',
                    activeSection === 'home' && window.location.pathname === '/'
                      ? 'opacity-100'
                      : 'opacity-0 group-hover:opacity-100'
                  ].join(' ')}
                ></span>
              </a>
              <a 
                href="/services" 
                onClick={(e) => handleNavClick(e, 'services')} 
                className={getNavItemClasses('services')}
              >
                <span className="relative z-10">Services</span>
                <span
                  className={[
                    'absolute inset-0 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full transition-opacity duration-200',
                    window.location.pathname === '/services' || (activeSection === 'services' && window.location.pathname === '/')
                      ? 'opacity-100'
                      : 'opacity-0 group-hover:opacity-100'
                  ].join(' ')}
                ></span>
              </a>
              <a 
                href="#team" 
                onClick={(e) => handleNavClick(e, 'team')} 
                className={getNavItemClasses('team')}
              >
                <span className="relative z-10">Professionals</span>
                <span
                  className={[
                    'absolute inset-0 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full transition-opacity duration-200',
                    activeSection === 'team' && window.location.pathname === '/'
                      ? 'opacity-100'
                      : 'opacity-0 group-hover:opacity-100'
                  ].join(' ')}
                ></span>
              </a>
              <a 
                href="#about" 
                onClick={(e) => handleNavClick(e, 'about')} 
                className={getNavItemClasses('about')}
              >
                <span className="relative z-10">About</span>
                <span
                  className={[
                    'absolute inset-0 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full transition-opacity duration-200',
                    activeSection === 'about' && window.location.pathname === '/'
                      ? 'opacity-100'
                      : 'opacity-0 group-hover:opacity-100'
                  ].join(' ')}
                ></span>
              </a>
              <a 
                href="#reviews" 
                onClick={(e) => handleNavClick(e, 'reviews')} 
                className={getNavItemClasses('reviews')}
              >
                <span className="relative z-10">Reviews</span>
                <span
                  className={[
                    'absolute inset-0 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full transition-opacity duration-200',
                    activeSection === 'reviews' && window.location.pathname === '/'
                      ? 'opacity-100'
                      : 'opacity-0 group-hover:opacity-100'
                  ].join(' ')}
                ></span>
              </a>
            </div>
          </div>
          )}

          <div className={`hidden md:flex flex-shrink-0 items-center space-x-2 ${isRecoveryAuthRoute ? 'ml-auto' : 'ml-6'}`}>
            {isRecoveryAuthRoute ? (
              <span className="text-sm text-gray-500 font-medium px-2">Password Reset</span>
            ) : showAuthenticatedNav ? (
              <>
                {/* Favorites Icon Button */}
                {!isDashboardRoute && (
                <button
                  type="button"
                  onClick={() => {
                    setSidebarType('favorites')
                    setSidebarOpen(true)
                  }}
                  className="relative w-10 h-10 flex items-center justify-center bg-white rounded-full hover:bg-gray-50 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-110 active:scale-95 border border-gray-200"
                  title="Favorites"
                >
                  <i className="fas fa-heart text-primary text-lg"></i>
                  {((favorites?.specialists?.length > 0 || favorites?.services?.length > 0) || (favoriteAppointments && favoriteAppointments.length > 0)) && (
                    <span className="absolute -top-1 -right-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg">
                      {(favorites?.specialists?.length || 0) + (favorites?.services?.length || 0) + (favoriteAppointments?.length || 0)}
                    </span>
                  )}
                </button>
                )}

                {/* Notifications Icon Button */}
                {!isDashboardRoute && (
                <button
                  type="button"
                  onClick={() => {
                    if (onNotificationsClick) {
                      onNotificationsClick()
                    } else {
                      // Open notifications sidebar
                      if (user) {
                        setSidebarType('notifications')
                        setSidebarOpen(true)
                      } else if (onAuthClick) {
                        onAuthClick()
                      }
                    }
                  }}
                  className="relative w-10 h-10 flex items-center justify-center bg-white rounded-full hover:bg-gray-50 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-110 active:scale-95 border border-gray-200"
                  title="Notifications"
                >
                  <i className="fas fa-bell text-primary text-lg"></i>
                  {unreadNotificationCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold rounded-full min-w-[1.25rem] h-5 flex items-center justify-center shadow-lg px-1">
                      {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                    </span>
                  )}
                </button>
                )}
                
                {/* Bookings Icon Button */}
                {!isDashboardRoute && (
                <button
                  type="button"
                  onClick={() => {
                    setSidebarType('bookings')
                    setSidebarOpen(true)
                  }}
                  className="relative w-10 h-10 flex items-center justify-center bg-white rounded-full hover:bg-gray-50 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-110 active:scale-95 border border-gray-200"
                  title="My Bookings"
                >
                  <i className="fas fa-calendar-check text-primary text-lg"></i>
                  {(() => {
                    // Calculate upcoming appointments count
                    if (!appointments || appointments.length === 0) return null
                    
                    const upcomingCount = appointments.filter(apt => {
                      if (salonId && (apt.salon_id || apt.salonId) && (apt.salon_id || apt.salonId) !== salonId) {
                        return false
                      }
                      // Exclude completed, cancelled, no_show, and missed
                      if (apt.status === 'completed' || apt.status === 'cancelled' || apt.status === 'no_show' || apt.status === 'missed') {
                        return false
                      }
                      // Include upcoming, confirmed, and pending
                      return apt.status === 'upcoming' || apt.status === 'confirmed' || apt.status === 'pending'
                    }).length
                    
                    return upcomingCount > 0 ? (
                      <span className="absolute -top-1 -right-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg">
                        {upcomingCount}
                      </span>
                    ) : null
                  })()}
                </button>
                )}
                
                {/* User Dropdown Button */}
                <div className="relative" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    console.log('Dropdown clicked! Current state:', showUserMenu)
                    setShowUserMenu(!showUserMenu)
                  }}
                  className="flex items-center bg-gradient-to-r from-gray-900 to-gray-800 text-white px-4 lg:px-6 py-2.5 rounded-full hover:from-gray-800 hover:to-gray-700 transition-all duration-200 text-sm lg:text-base cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95"
                >
                  {profileImageUrl && !avatarFailed ? (
                    <img 
                      src={profileImageUrl} 
                      alt="Profile" 
                      className="w-7 h-7 rounded-full mr-2 object-cover border-2 border-white/50 ring-2 ring-purple-500/30"
                      onError={() => setAvatarFailed(true)}
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full mr-2 bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center">
                      <i className="fas fa-user text-xs text-white"></i>
                    </div>
                  )}
                  <span className="font-medium">{profile?.first_name || user?.user_metadata?.first_name || 'Account'}</span>
                  <i className={`fas fa-chevron-down ml-2 text-xs transition-transform duration-300 ${showUserMenu ? 'rotate-180' : ''}`}></i>
                </button>
                
                {showUserMenu && (
                  <div className="absolute right-0 mt-3 w-56 bg-white/95 backdrop-blur-md rounded-xl shadow-2xl border border-gray-200/50 py-2 z-[100] animate-slideDown overflow-hidden">
                    <div className="px-2">
                      <button
                        type="button"
                        onClick={() => {
                          console.log('Dashboard clicked')
                          navigate('/dashboard')
                          setShowUserMenu(false)
                        }}
                        className="w-full text-left px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 transition-all duration-200 flex items-center rounded-lg group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center mr-3 group-hover:scale-110 transition-transform">
                          <i className="fas fa-th-large text-primary text-sm"></i>
                        </div>
                        <span className="font-medium">Dashboard</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          console.log('Settings clicked')
                          navigate('/dashboard?tab=settings')
                          setShowUserMenu(false)
                        }}
                        className="w-full text-left px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 transition-all duration-200 flex items-center rounded-lg group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center mr-3 group-hover:scale-110 transition-transform">
                          <i className="fas fa-cog text-primary text-sm"></i>
                        </div>
                        <span className="font-medium">Settings</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          navigate('/dashboard?tab=support&compose=1')
                          setShowUserMenu(false)
                        }}
                        className="w-full text-left px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 transition-all duration-200 flex items-center rounded-lg group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center mr-3 group-hover:scale-110 transition-transform">
                          <i className="fas fa-headset text-primary text-sm"></i>
                        </div>
                        <span className="font-medium">Support</span>
                      </button>
                    </div>
                    <div className="border-t border-gray-200 my-2"></div>
                    <div className="px-2">
                      <button
                        type="button"
                        onClick={() => {
                          console.log('Logout clicked')
                          handleLogout()
                        }}
                        className="w-full text-left px-4 py-3 text-red-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 transition-all duration-200 flex items-center rounded-lg group font-medium"
                      >
                        <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center mr-3 group-hover:scale-110 transition-transform">
                          <i className="fas fa-sign-out-alt text-red-600 text-sm"></i>
                        </div>
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              </>
            ) : (
            <button 
                type="button"
                onClick={onAuthClick}
              className="bg-gradient-to-r from-gray-900 to-gray-800 text-white px-5 lg:px-7 py-2.5 rounded-full hover:from-gray-800 hover:to-gray-700 transition-all duration-200 text-sm lg:text-base font-medium shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95"
            >
              <i className="fas fa-user mr-2"></i>
                <span>Sign In</span>
            </button>
            )}
          </div>
          
          {/* Mobile Menu Button — hidden on password reset page */}
          {!isRecoveryAuthRoute && (
          <button 
            onClick={() => setMobileMenu(!mobileMenu)} 
            className="md:hidden text-gray-700 ml-auto w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-all duration-200 hover:scale-110 active:scale-95"
          >
            <i className={`fas ${mobileMenu ? 'fa-times' : 'fa-bars'} text-xl transition-transform duration-300`}></i>
          </button>
          )}
          {isRecoveryAuthRoute && (
            <span className="md:hidden ml-auto text-sm text-gray-500 font-medium">Password Reset</span>
          )}
        </div>
        
        {/* Mobile Menu */}
        {!isRecoveryAuthRoute && mobileMenu && (
          <div className="md:hidden pb-4 pt-2 animate-slideDown border-t border-gray-100">
            {salons.length > 1 && (
              <div className="px-4 py-3 border-b border-gray-100">
                <SalonBranchDropdown
                  className="w-full"
                  size="sm"
                  salons={salons}
                  selectedSalon={selectedSalon}
                  onSelect={(next) => {
                    handleBranchSelect(next)
                    setMobileMenu(false)
                  }}
                />
              </div>
            )}
            <div className="space-y-1">
              <a 
                href="#home" 
                onClick={(e) => handleNavClick(e, 'home')} 
                className="block px-4 py-3 text-gray-700 hover:text-primary hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 transition-all duration-200 cursor-pointer rounded-lg font-medium"
              >
                <i className="fas fa-home mr-3 text-primary"></i>Home
              </a>
              <a 
                href="/services" 
                onClick={(e) => handleNavClick(e, 'services')} 
                className="block px-4 py-3 text-gray-700 hover:text-primary hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 transition-all duration-200 cursor-pointer rounded-lg font-medium"
              >
                <i className="fas fa-spa mr-3 text-primary"></i>Services
              </a>
              <a 
                href="#team" 
                onClick={(e) => handleNavClick(e, 'team')} 
                className="block px-4 py-3 text-gray-700 hover:text-primary hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 transition-all duration-200 cursor-pointer rounded-lg font-medium"
              >
                <i className="fas fa-users mr-3 text-primary"></i>Professionals
              </a>
              <a 
                href="#about" 
                onClick={(e) => handleNavClick(e, 'about')} 
                className="block px-4 py-3 text-gray-700 hover:text-primary hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 transition-all duration-200 cursor-pointer rounded-lg font-medium"
              >
                <i className="fas fa-info-circle mr-3 text-primary"></i>About
              </a>
              <a 
                href="#reviews" 
                onClick={(e) => handleNavClick(e, 'reviews')} 
                className="block px-4 py-3 text-gray-700 hover:text-primary hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 transition-all duration-200 cursor-pointer rounded-lg font-medium"
              >
                <i className="fas fa-star mr-3 text-primary"></i>Reviews
              </a>
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-200">
              {isRecoveryAuthRoute ? (
                <p className="text-center text-sm text-gray-500 font-medium py-2">Password Reset</p>
              ) : showAuthenticatedNav ? (
                <>
                  {!isDashboardRoute && (
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <button 
                      onClick={() => {
                        setSidebarType('favorites')
                        setSidebarOpen(true)
                        setMobileMenu(false)
                      }}
                      className="relative flex items-center justify-center bg-white border border-gray-200 text-primary px-4 py-3 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium shadow-md hover:shadow-lg"
                    >
                      <i className="fas fa-heart mr-2"></i>
                      <span>Favorites</span>
                      {((favorites?.specialists?.length > 0 || favorites?.services?.length > 0) || (favoriteAppointments && favoriteAppointments.length > 0)) && (
                        <span className="ml-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                          {(favorites?.specialists?.length || 0) + (favorites?.services?.length || 0) + (favoriteAppointments?.length || 0)}
                        </span>
                      )}
                    </button>
                    <button 
                      onClick={() => {
                        setSidebarType('bookings')
                        setSidebarOpen(true)
                        setMobileMenu(false)
                      }}
                      className="relative flex items-center justify-center bg-white border border-gray-200 text-primary px-4 py-3 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium shadow-md hover:shadow-lg"
                    >
                      <i className="fas fa-calendar-check mr-2"></i>
                      <span>Bookings</span>
                      {appointments && appointments.length > 0 && (
                        <span className="ml-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                          {appointments.length}
                        </span>
                      )}
                    </button>
                  </div>
                  )}
                  <button 
                    onClick={() => {
                      navigate('/dashboard')
                      setMobileMenu(false)
                    }}
                    className="w-full bg-gradient-to-r from-gray-900 to-gray-800 text-white px-4 py-3 rounded-lg hover:from-gray-800 hover:to-gray-700 transition-all duration-200 text-left font-medium shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-95"
                  >
                    <i className="fas fa-th-large mr-2"></i>
                    <span>Dashboard</span>
                  </button>
                  <button 
                    onClick={() => {
                      navigate('/dashboard?tab=support&compose=1')
                      setMobileMenu(false)
                    }}
                    className="w-full mt-2 bg-white border border-gray-200 text-primary px-4 py-3 rounded-lg hover:bg-gray-50 transition-all duration-200 text-left font-medium shadow-md hover:shadow-lg"
                  >
                    <i className="fas fa-headset mr-2"></i>
                    <span>Support</span>
                  </button>
                  <button 
                    onClick={() => {
                      handleLogout()
                      setMobileMenu(false)
                    }}
                    className="w-full mt-2 bg-red-50 text-red-600 px-4 py-3 rounded-lg hover:bg-red-100 transition-all duration-200 text-left font-medium border border-red-200 hover:border-red-300"
                  >
                    <i className="fas fa-sign-out-alt mr-2"></i>
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                <button 
                  onClick={() => {
                    onAuthClick()
                    setMobileMenu(false)
                  }}
                  className="w-full bg-gradient-to-r from-gray-900 to-gray-800 text-white px-4 py-3 rounded-lg hover:from-gray-800 hover:to-gray-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-95"
                >
                  <i className="fas fa-user mr-2"></i>
                  <span>Sign In</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
    
    {/* Sidebar - Render outside nav to avoid z-index and positioning issues.
        Hidden on /dashboard routes to prevent having two different sidebars at once. */}
    {showAuthenticatedNav && !isDashboardRoute && sidebarProps && Object.keys(sidebarProps).length > 0 && (
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => {
          setSidebarOpen(false)
          setSidebarType(null)
        }}
        type={sidebarType}
        {...sidebarProps}
      />
    )}
  </>
  )
}

export default Navigation

